// src/pages/campaigns/forms/CampaignContentPlanForm.jsx
import React from 'react';
import {
  Grid,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Chip,
  Box,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormHelperText,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Layers as ContentIcon,
  CalendarMonth as FrequencyIcon,
  Tag as HashtagIcon,
  ViewColumn as PillarIcon,
  Palette as ThemeIcon,
  Schedule as ScheduleIcon,
  Lightbulb as TipsIcon,
  Info as InfoIcon,
} from '@mui/icons-material';

// Import FormSection component
import { FormSection } from '../../../design-system/components/forms';

// Content type options
const CONTENT_TYPES = [
  { value: 'post', label: 'Regular Post' },
  { value: 'story', label: 'Story' },
  { value: 'reel', label: 'Reel' },
  { value: 'video', label: 'Video' },
  { value: 'article', label: 'Article' },
  { value: 'carousel', label: 'Carousel' },
  { value: 'announcement', label: 'Announcement' },
  { value: 'promotion', label: 'Promotion' },
  { value: 'event', label: 'Event' },
  { value: 'poll', label: 'Poll' },
  { value: 'contest', label: 'Contest' },
  { value: 'other', label: 'Other' }
];

// Frequency options
const FREQUENCY_OPTIONS = [
  { value: 'daily', label: 'Daily' },
  { value: 'alternate_days', label: 'Alternate Days' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'custom', label: 'Custom' }
];

// Common content pillar suggestions
const PILLAR_SUGGESTIONS = [
  'Education', 'Entertainment', 'Inspiration', 'Promotion', 
  'Behind the Scenes', 'User Generated Content', 'Product Features',
  'Brand Values', 'Community', 'Testimonials', 'Industry News'
];

// Campaign content plan form component
const CampaignContentPlanForm = ({
  campaignData,
  onChange,
  onNestedChange,
  errors = {},
  touched = {}
}) => {
  const theme = useTheme();

  // Get content plan data or initialize
  const contentPlan = campaignData.contentPlan || {
    frequency: 'weekly',
    customFrequency: 0,
    totalPlannedPosts: 0,
    contentTypes: [],
    contentPillars: [],
    contentThemes: [],
    hashtags: []
  };

  // Handle frequency change
  const handleFrequencyChange = (e) => {
    onNestedChange('contentPlan', 'frequency', e.target.value);
  };

  // Handle custom frequency change
  const handleCustomFrequencyChange = (e) => {
    onNestedChange('contentPlan', 'customFrequency', parseInt(e.target.value, 10) || 0);
  };

  // Handle total planned posts change
  const handleTotalPlannedPostsChange = (e) => {
    onNestedChange('contentPlan', 'totalPlannedPosts', parseInt(e.target.value, 10) || 0);
  };

  // Handle content types change
  const handleContentTypesChange = (event, newValue) => {
    onNestedChange('contentPlan', 'contentTypes', newValue);
  };

  // Handle content pillars change
  const handleContentPillarsChange = (event, newValue) => {
    onNestedChange('contentPlan', 'contentPillars', newValue);
  };

  // Handle content themes change
  const handleContentThemesChange = (event, newValue) => {
    onNestedChange('contentPlan', 'contentThemes', newValue);
  };

  // Handle hashtags change
  const handleHashtagsChange = (event, newValue) => {
    onNestedChange('contentPlan', 'hashtags', newValue);
  };

  // Function to render posting schedule fields
  const renderPostingSchedule = () => (
    <Grid container spacing={3}>
      {/* Posting Frequency */}
      <Grid item xs={12} md={6}>
        <FormControl 
          fullWidth
          error={touched?.['contentPlan.frequency'] && Boolean(errors?.['contentPlan.frequency'])}
        >
          <InputLabel 
            id="frequency-label"
            error={touched?.['contentPlan.frequency'] && Boolean(errors?.['contentPlan.frequency'])}
          >
            Posting Frequency
          </InputLabel>
          <Select
            labelId="frequency-label"
            id="frequency"
            value={contentPlan.frequency || 'weekly'}
            onChange={handleFrequencyChange}
            label="Posting Frequency"
            error={touched?.['contentPlan.frequency'] && Boolean(errors?.['contentPlan.frequency'])}
            startAdornment={
              <FrequencyIcon color="action" sx={{ ml: 1, mr: 1 }} />
            }
          >
            {FREQUENCY_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
          {touched?.['contentPlan.frequency'] && errors?.['contentPlan.frequency'] && (
            <FormHelperText error>{errors['contentPlan.frequency']}</FormHelperText>
          )}
        </FormControl>

        {contentPlan.frequency === 'custom' && (
          <TextField
            label="Custom Frequency (posts per month)"
            type="number"
            value={contentPlan.customFrequency || ''}
            onChange={handleCustomFrequencyChange}
            fullWidth
            margin="normal"
            inputProps={{ min: 1, max: 100 }}
          />
        )}
      </Grid>

      {/* Total Planned Posts */}
      <Grid item xs={12} md={6}>
        <TextField
          label="Total Planned Posts *"
          type="number"
          value={contentPlan.totalPlannedPosts || ''}
          onChange={handleTotalPlannedPostsChange}
          fullWidth
          required
          error={touched?.['contentPlan.totalPlannedPosts'] && Boolean(errors?.['contentPlan.totalPlannedPosts'])}
          helperText={touched?.['contentPlan.totalPlannedPosts'] && errors?.['contentPlan.totalPlannedPosts'] ? 
            errors?.['contentPlan.totalPlannedPosts'] : 
            "Estimated total number of posts for the entire campaign"}
          inputProps={{ min: 0 }}
          InputLabelProps={{
            error: touched?.['contentPlan.totalPlannedPosts'] && Boolean(errors?.['contentPlan.totalPlannedPosts']),
            required: true,
          }}
        />
      </Grid>
    </Grid>
  );

  // Function to render content strategy fields
  const renderContentStrategy = () => (
    <Grid container spacing={3}>
      {/* Content Types */}
      <Grid item xs={12}>
        <Autocomplete
          multiple
          id="contentTypes"
          options={CONTENT_TYPES}
          getOptionLabel={(option) => typeof option === 'string' ? option : option.label}
          value={contentPlan.contentTypes || []}
          onChange={handleContentTypesChange}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                label={typeof option === 'string' ? option : option.label}
                {...getTagProps({ index })}
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Content Types *"
              placeholder="Select content types"
              fullWidth
              required
              error={touched?.['contentPlan.contentTypes'] && Boolean(errors?.['contentPlan.contentTypes'])}
              helperText={touched?.['contentPlan.contentTypes'] && errors?.['contentPlan.contentTypes'] ? 
                errors?.['contentPlan.contentTypes'] : 
                "Types of content to be created for this campaign"}
              InputLabelProps={{
                error: touched?.['contentPlan.contentTypes'] && Boolean(errors?.['contentPlan.contentTypes']),
                required: true,
              }}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    <ContentIcon color="action" sx={{ ml: 1, mr: 1 }} />
                    {params.InputProps.startAdornment}
                  </>
                )
              }}
            />
          )}
        />
      </Grid>

      {/* Content Pillars & Themes */}
      <Grid item xs={12} md={6}>
        <Autocomplete
          multiple
          id="contentPillars"
          options={PILLAR_SUGGESTIONS}
          freeSolo
          value={contentPlan.contentPillars || []}
          onChange={handleContentPillarsChange}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                icon={<PillarIcon />}
                label={option}
                {...getTagProps({ index })}
                color="secondary"
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Content Pillars"
              placeholder="Add content pillars"
              fullWidth
              error={touched?.['contentPlan.contentPillars'] && Boolean(errors?.['contentPlan.contentPillars'])}
              helperText={touched?.['contentPlan.contentPillars'] && errors?.['contentPlan.contentPillars'] ? 
                errors?.['contentPlan.contentPillars'] : 
                "Main content categories that support your campaign objectives"}
              InputLabelProps={{
                error: touched?.['contentPlan.contentPillars'] && Boolean(errors?.['contentPlan.contentPillars']),
              }}
            />
          )}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <Autocomplete
          multiple
          id="contentThemes"
          options={[]}
          freeSolo
          value={contentPlan.contentThemes || []}
          onChange={handleContentThemesChange}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                icon={<ThemeIcon />}
                label={option}
                {...getTagProps({ index })}
                color="info"
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Content Themes"
              placeholder="Add content themes"
              fullWidth
              helperText="Specific themes or topics to cover in your content"
            />
          )}
        />
      </Grid>
    </Grid>
  );

  // Function to render hashtags section
  const renderHashtags = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Autocomplete
          multiple
          id="hashtags"
          options={[]}
          freeSolo
          value={contentPlan.hashtags || []}
          onChange={handleHashtagsChange}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                icon={<HashtagIcon />}
                label={option}
                {...getTagProps({ index })}
                variant="outlined"
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Campaign Hashtags"
              placeholder="Add hashtags (without #)"
              fullWidth
              helperText="Hashtags to use consistently across campaign content"
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    <HashtagIcon color="action" sx={{ ml: 1, mr: 1 }} />
                    {params.InputProps.startAdornment}
                  </>
                )
              }}
            />
          )}
        />
      </Grid>
    </Grid>
  );

  // Function to render content planning tips
  const renderContentPlanningTips = () => (
    <Box sx={{ 
      p: 2, 
      bgcolor: alpha(theme.palette.info.main, 0.1), 
      borderRadius: 1
    }}>
      <Typography variant="subtitle2" gutterBottom>
        Content Planning Tips:
      </Typography>
      <Typography variant="body2">
        • Maintain a consistent posting schedule to build audience expectation
      </Typography>
      <Typography variant="body2">
        • Use a mix of content types to keep your audience engaged
      </Typography>
      <Typography variant="body2">
        • Align content pillars with your campaign objectives and brand values
      </Typography>
      <Typography variant="body2">
        • Create a content calendar to plan and organize your posts ahead of time
      </Typography>
      <Typography variant="body2">
        • Use campaign-specific hashtags consistently to track engagement
      </Typography>
    </Box>
  );

  return (
    <>
      {/* Section 1: Posting Schedule */}
      <FormSection
        title="Posting Schedule"
        description="Define how often you'll post content during this campaign."
        icon={<ScheduleIcon />}
        tooltip="Set your content cadence and volume to maintain consistent presence during the campaign."
      >
        {renderPostingSchedule()}
      </FormSection>

      {/* Section 2: Content Strategy */}
      <FormSection
        title="Content Strategy"
        description="Select the types of content and key themes for your campaign."
        icon={<ContentIcon />}
        tooltip="Define your content mix and organizing frameworks to ensure message consistency."
      >
        {renderContentStrategy()}
      </FormSection>

      {/* Section 3: Campaign Hashtags */}
      <FormSection
        title="Campaign Hashtags"
        description="Create branded and campaign-specific hashtags for tracking and discoverability."
        icon={<HashtagIcon />}
        tooltip="Hashtags help increase content discoverability and provide a way to track campaign performance."
      >
        {renderHashtags()}
      </FormSection>

      {/* Section 4: Content Planning Tips */}
      <FormSection
        title="Content Planning Best Practices"
        description="Recommendations for more effective content planning."
        icon={<TipsIcon />}
        tooltip="Tips to help you create a more effective content strategy for your campaign."
      >
        {renderContentPlanningTips()}
      </FormSection>
    </>
  );
};

export default CampaignContentPlanForm;